import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { cloneDeep, set, get } from "lodash";
import { useContent } from "@/context/ContentContext";
import { useAuth } from "@/context/AuthContext";
import { TextField, TextArea, Panel } from "@/components/admin/Field";
import ImageUpload from "@/components/admin/ImageUpload";
import { toast } from "sonner";
import {
  LayoutDashboard, Image as ImageIcon, UtensilsCrossed, Users,
  BookOpen, HelpCircle, MapPin, PanelBottom, Type, LogOut,
  Save, ExternalLink, Loader2, Plus, Trash2, Megaphone,
} from "lucide-react";

const SECTIONS = [
  { key: "brand", label: "Marca", icon: Type },
  { key: "header", label: "Botões de Topo", icon: LayoutDashboard },
  { key: "hero", label: "Banner (Hero)", icon: ImageIcon },
  { key: "categories", label: "Categorias", icon: UtensilsCrossed },
  { key: "marquee", label: "Faixa de Texto", icon: Megaphone },
  { key: "team", label: "Nossa Equipe", icon: Users },
  { key: "manifesto", label: "Quem Somos", icon: BookOpen },
  { key: "faq", label: "Dúvidas Frequentes", icon: HelpCircle },
  { key: "contact", label: "Fale Conosco", icon: MapPin },
  { key: "footer", label: "Rodapé", icon: PanelBottom },
];

export default function AdminDashboard() {
  const { content, save } = useContent();
  const { logout, user } = useAuth();
  const [draft, setDraft] = useState(null);
  const [active, setActive] = useState("brand");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (content) setDraft(cloneDeep(content));
  }, [content]);

  if (!draft) {
    return (
      <div className="min-h-screen grid place-items-center bg-stone-100">
        <Loader2 className="h-7 w-7 animate-spin text-stone-500" />
      </div>
    );
  }

  const upd = (path, value) => {
    setDraft((prev) => {
      const next = cloneDeep(prev);
      set(next, path, value);
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await save(draft);
      toast.success("Alterações salvas com sucesso!");
    } catch (e) {
      toast.error("Erro ao salvar. Tente novamente.");
    } finally {
      setSaving(false);
    }
  };

  const activeMeta = SECTIONS.find((s) => s.key === active);

  return (
    <div className="min-h-screen bg-stone-100 text-stone-900 flex">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 shrink-0 flex-col border-r border-stone-200 bg-white sticky top-0 h-screen">
        <div className="px-6 py-6 border-b border-stone-200">
          <p className="font-serif text-2xl">{draft.brand?.name || "Aurora"}</p>
          <p className="text-xs tracking-[0.2em] uppercase text-stone-400 mt-1">CMS · Painel</p>
        </div>
        <nav className="flex-1 overflow-y-auto admin-scroll p-3 space-y-1">
          {SECTIONS.map((s) => (
            <button
              key={s.key}
              data-testid={`nav-${s.key}`}
              onClick={() => setActive(s.key)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-200 ${
                active === s.key
                  ? "bg-stone-950 text-stone-50"
                  : "text-stone-600 hover:bg-stone-100"
              }`}
            >
              <s.icon className="h-4 w-4" />
              {s.label}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-stone-200">
          <button
            data-testid="admin-logout"
            onClick={logout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-stone-600 hover:bg-stone-100 transition-colors duration-200"
          >
            <LogOut className="h-4 w-4" /> Sair
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Top bar */}
        <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-stone-200 px-5 md:px-8 py-4 flex items-center justify-between gap-4">
          <div>
            <h1 className="font-semibold text-lg flex items-center gap-2">
              {activeMeta && <activeMeta.icon className="h-5 w-5 text-stone-400" />}
              {activeMeta?.label}
            </h1>
            <p className="text-xs text-stone-400">Logado como {user?.email}</p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/"
              target="_blank"
              data-testid="view-site-link"
              className="inline-flex items-center gap-2 rounded-md border border-stone-300 px-4 py-2 text-sm font-medium hover:bg-stone-50 transition-colors duration-300"
            >
              <ExternalLink className="h-4 w-4" /> Ver site
            </Link>
            <button
              data-testid="admin-save"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-md bg-amber-700 text-stone-50 px-5 py-2 text-sm font-medium hover:bg-amber-600 transition-colors duration-300 disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Salvar
            </button>
            <button
              data-testid="admin-logout-top"
              onClick={logout}
              title="Sair"
              className="inline-flex items-center gap-2 rounded-md border border-stone-300 px-3 md:px-4 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50 transition-colors duration-300"
            >
              <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Sair</span>
            </button>
          </div>
        </header>

        {/* Mobile section selector */}
        <div className="md:hidden px-5 py-3 border-b border-stone-200 bg-white overflow-x-auto flex gap-2">
          {SECTIONS.map((s) => (
            <button
              key={s.key}
              onClick={() => setActive(s.key)}
              className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-medium ${
                active === s.key ? "bg-stone-950 text-stone-50" : "bg-stone-100 text-stone-600"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        <main className="flex-1 p-5 md:p-8 max-w-4xl w-full mx-auto space-y-6">
          {renderSection(active, draft, upd)}
        </main>
      </div>
    </div>
  );
}

function ArrayControls({ onAdd, addLabel }) {
  return (
    <button
      type="button"
      onClick={onAdd}
      data-testid="array-add"
      className="inline-flex items-center gap-2 rounded-md border border-stone-300 px-4 py-2 text-sm font-medium hover:bg-stone-50 transition-colors duration-300"
    >
      <Plus className="h-4 w-4" /> {addLabel}
    </button>
  );
}

function renderSection(active, draft, upd) {
  switch (active) {
    case "brand":
      return (
        <Panel title="Identidade da Marca" description="Nome, assinatura e logomarca exibidos no cabeçalho e rodapé.">
          <div>
            <span className="block text-xs font-semibold tracking-[0.1em] uppercase text-stone-500 mb-2">
              Logomarca (opcional — substitui o nome no topo)
            </span>
            <ImageUpload value={draft.brand?.logo} onChange={(v) => upd("brand.logo", v)} testid="brand-logo" aspect="aspect-[3/1]" />
          </div>
          <TextField label="Nome do restaurante" value={draft.brand?.name} onChange={(v) => upd("brand.name", v)} testid="field-brand-name" />
          <TextField label="Assinatura / Tagline" value={draft.brand?.tagline} onChange={(v) => upd("brand.tagline", v)} testid="field-brand-tagline" />
        </Panel>
      );

    case "header":
      return (
        <Panel title="Botões de Destaque" description="Configure os textos e os links externos dos botões 'Peça e Retire' e 'Delivery'.">
          <div className="grid sm:grid-cols-2 gap-5">
            <TextField label="Texto — Botão 1" value={draft.header?.orderButton?.label} onChange={(v) => upd("header.orderButton.label", v)} testid="field-order-label" />
            <TextField label="URL — Botão 1" value={draft.header?.orderButton?.url} onChange={(v) => upd("header.orderButton.url", v)} testid="field-order-url" placeholder="https://..." />
            <TextField label="Texto — Botão 2" value={draft.header?.deliveryButton?.label} onChange={(v) => upd("header.deliveryButton.label", v)} testid="field-delivery-label" />
            <TextField label="URL — Botão 2" value={draft.header?.deliveryButton?.url} onChange={(v) => upd("header.deliveryButton.url", v)} testid="field-delivery-url" placeholder="https://..." />
          </div>
        </Panel>
      );

    case "hero":
      return (
        <Panel title="Banner Principal" description="Imagem de fundo e textos de chamada da área central.">
          <ImageUpload value={draft.hero?.backgroundImage} onChange={(v) => upd("hero.backgroundImage", v)} testid="hero-image" aspect="aspect-[16/7]" />
          <TextField label="Selo (eyebrow)" value={draft.hero?.eyebrow} onChange={(v) => upd("hero.eyebrow", v)} testid="field-hero-eyebrow" />
          <TextArea label="Título (use quebras de linha)" value={draft.hero?.headline} onChange={(v) => upd("hero.headline", v)} testid="field-hero-headline" rows={3} />
          <TextArea label="Subtítulo" value={draft.hero?.subheadline} onChange={(v) => upd("hero.subheadline", v)} testid="field-hero-sub" />
          <div className="grid sm:grid-cols-2 gap-5">
            <TextField label="Texto do botão" value={draft.hero?.ctaLabel} onChange={(v) => upd("hero.ctaLabel", v)} testid="field-hero-cta-label" />
            <TextField label="Link do botão" value={draft.hero?.ctaUrl} onChange={(v) => upd("hero.ctaUrl", v)} testid="field-hero-cta-url" />
          </div>
        </Panel>
      );

    case "categories":
      return (
        <div className="space-y-6">
          <Panel title="Cabeçalho da Seção">
            <TextField label="Rótulo" value={draft.categories?.label} onChange={(v) => upd("categories.label", v)} testid="field-cat-label" />
          </Panel>
          {(draft.categories?.items || []).map((item, i) => (
            <Panel key={item.id || i} title={`Categoria ${i + 1}`}>
              <ImageUpload value={item.image} onChange={(v) => upd(`categories.items[${i}].image`, v)} testid={`cat-image-${i}`} aspect="aspect-square" />
              <TextField label="Título" value={item.title} onChange={(v) => upd(`categories.items[${i}].title`, v)} testid={`field-cat-title-${i}`} />
              <TextField label="Descrição" value={item.description} onChange={(v) => upd(`categories.items[${i}].description`, v)} testid={`field-cat-desc-${i}`} />
              <button
                type="button"
                data-testid={`cat-remove-${i}`}
                onClick={() => upd("categories.items", get(draft, "categories.items").filter((_, idx) => idx !== i))}
                className="inline-flex items-center gap-2 text-sm text-red-600 hover:text-red-700 transition-colors duration-200"
              >
                <Trash2 className="h-4 w-4" /> Remover categoria
              </button>
            </Panel>
          ))}
          <ArrayControls
            addLabel="Adicionar categoria"
            onAdd={() =>
              upd("categories.items", [
                ...(get(draft, "categories.items") || []),
                { id: `c${Date.now()}`, title: "Nova Categoria", description: "", image: "" },
              ])
            }
          />
        </div>
      );

    case "marquee":
      return (
        <Panel title="Faixa de Texto (Marquee)" description="Texto em movimento entre as seções.">
          <TextField label="Texto" value={draft.marquee?.text} onChange={(v) => upd("marquee.text", v)} testid="field-marquee-text" />
        </Panel>
      );

    case "team":
      return (
        <Panel title="Nossa Equipe / Destaque do Mês" description="Homenageie um colaborador com fotos, nome, cargo e texto.">
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <span className="block text-xs font-semibold tracking-[0.1em] uppercase text-stone-500 mb-2">Foto principal</span>
              <ImageUpload value={draft.team?.photo} onChange={(v) => upd("team.photo", v)} testid="team-image" aspect="aspect-[4/5]" />
            </div>
            <div>
              <span className="block text-xs font-semibold tracking-[0.1em] uppercase text-stone-500 mb-2">Foto secundária</span>
              <ImageUpload value={draft.team?.photo2} onChange={(v) => upd("team.photo2", v)} testid="team-image2" aspect="aspect-square" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <TextField label="Rótulo da seção" value={draft.team?.label} onChange={(v) => upd("team.label", v)} testid="field-team-label" />
            <TextField label="Selo (ex: Destaque do Mês)" value={draft.team?.title} onChange={(v) => upd("team.title", v)} testid="field-team-title" />
            <TextField label="Nome do colaborador" value={draft.team?.name} onChange={(v) => upd("team.name", v)} testid="field-team-name" />
            <TextField label="Cargo" value={draft.team?.role} onChange={(v) => upd("team.role", v)} testid="field-team-role" />
          </div>
          <TextArea label="Texto de homenagem" value={draft.team?.tribute} onChange={(v) => upd("team.tribute", v)} testid="field-team-tribute" rows={4} />
        </Panel>
      );

    case "manifesto":
      return (
        <div className="space-y-6">
          <Panel title="Quem Somos — Cabeçalho">
            <TextField label="Rótulo" value={draft.manifesto?.label} onChange={(v) => upd("manifesto.label", v)} testid="field-manifesto-label" />
            <TextField label="Título" value={draft.manifesto?.title} onChange={(v) => upd("manifesto.title", v)} testid="field-manifesto-title" />
          </Panel>
          {(draft.manifesto?.chapters || []).map((ch, i) => (
            <Panel key={i} title={`Capítulo ${i + 1}`}>
              <div className="grid sm:grid-cols-2 gap-5">
                <TextField label="Número" value={ch.number} onChange={(v) => upd(`manifesto.chapters[${i}].number`, v)} testid={`field-chapter-num-${i}`} />
                <TextField label="Título" value={ch.title} onChange={(v) => upd(`manifesto.chapters[${i}].title`, v)} testid={`field-chapter-title-${i}`} />
              </div>
              <TextArea label="Texto" value={ch.text} onChange={(v) => upd(`manifesto.chapters[${i}].text`, v)} testid={`field-chapter-text-${i}`} />
              <button
                type="button"
                data-testid={`chapter-remove-${i}`}
                onClick={() => upd("manifesto.chapters", get(draft, "manifesto.chapters").filter((_, idx) => idx !== i))}
                className="inline-flex items-center gap-2 text-sm text-red-600 hover:text-red-700 transition-colors duration-200"
              >
                <Trash2 className="h-4 w-4" /> Remover capítulo
              </button>
            </Panel>
          ))}
          <ArrayControls
            addLabel="Adicionar capítulo"
            onAdd={() =>
              upd("manifesto.chapters", [
                ...(get(draft, "manifesto.chapters") || []),
                { number: "0" + ((draft.manifesto?.chapters?.length || 0) + 1), title: "Novo Capítulo", text: "" },
              ])
            }
          />
        </div>
      );

    case "faq":
      return (
        <div className="space-y-6">
          <Panel title="Dúvidas Frequentes — Cabeçalho">
            <TextField label="Rótulo" value={draft.faq?.label} onChange={(v) => upd("faq.label", v)} testid="field-faq-label" />
            <TextField label="Título" value={draft.faq?.title} onChange={(v) => upd("faq.title", v)} testid="field-faq-title" />
          </Panel>
          {(draft.faq?.items || []).map((item, i) => (
            <Panel key={item.id || i} title={`Pergunta ${i + 1}`}>
              <TextField label="Pergunta" value={item.question} onChange={(v) => upd(`faq.items[${i}].question`, v)} testid={`field-faq-q-${i}`} />
              <TextArea label="Resposta" value={item.answer} onChange={(v) => upd(`faq.items[${i}].answer`, v)} testid={`field-faq-a-${i}`} />
              <button
                type="button"
                data-testid={`faq-remove-${i}`}
                onClick={() => upd("faq.items", get(draft, "faq.items").filter((_, idx) => idx !== i))}
                className="inline-flex items-center gap-2 text-sm text-red-600 hover:text-red-700 transition-colors duration-200"
              >
                <Trash2 className="h-4 w-4" /> Remover pergunta
              </button>
            </Panel>
          ))}
          <ArrayControls
            addLabel="Adicionar pergunta"
            onAdd={() =>
              upd("faq.items", [
                ...(get(draft, "faq.items") || []),
                { id: `f${Date.now()}`, question: "Nova pergunta", answer: "" },
              ])
            }
          />
        </div>
      );

    case "contact":
      return (
        <Panel title="Fale Conosco" description="Endereço físico e informações de contato.">
          <div className="grid sm:grid-cols-2 gap-5">
            <TextField label="Rótulo" value={draft.contact?.label} onChange={(v) => upd("contact.label", v)} testid="field-contact-label" />
            <TextField label="Título" value={draft.contact?.title} onChange={(v) => upd("contact.title", v)} testid="field-contact-title" />
          </div>
          <TextField label="Endereço" value={draft.contact?.address} onChange={(v) => upd("contact.address", v)} testid="field-contact-address" />
          <div className="grid sm:grid-cols-2 gap-5">
            <TextField label="Telefone" value={draft.contact?.phone} onChange={(v) => upd("contact.phone", v)} testid="field-contact-phone" />
            <TextField label="E-mail" value={draft.contact?.email} onChange={(v) => upd("contact.email", v)} testid="field-contact-email" />
          </div>
          <TextField label="Horários" value={draft.contact?.hours} onChange={(v) => upd("contact.hours", v)} testid="field-contact-hours" />
        </Panel>
      );

    case "footer":
      return (
        <Panel title="Rodapé" description="Mensagem, dados institucionais e redes sociais.">
          <TextArea label="Mensagem" value={draft.footer?.note} onChange={(v) => upd("footer.note", v)} testid="field-footer-note" rows={2} />
          <div className="grid sm:grid-cols-2 gap-5">
            <TextField label="CNPJ" value={draft.footer?.cnpj} onChange={(v) => upd("footer.cnpj", v)} testid="field-footer-cnpj" />
            <TextField label="SAC" value={draft.footer?.sac} onChange={(v) => upd("footer.sac", v)} testid="field-footer-sac" />
          </div>
          <TextField label="Endereço" value={draft.footer?.address} onChange={(v) => upd("footer.address", v)} testid="field-footer-address" />
          <div className="grid sm:grid-cols-2 gap-5">
            <TextField label="Instagram (URL)" value={draft.footer?.instagram} onChange={(v) => upd("footer.instagram", v)} testid="field-footer-instagram" />
            <TextField label="Facebook (URL)" value={draft.footer?.facebook} onChange={(v) => upd("footer.facebook", v)} testid="field-footer-facebook" />
          </div>
        </Panel>
      );

    default:
      return null;
  }
}
